import React from 'react';
import { useExam } from '../contexts/ExamContext';
import { getExamColors, getExamSubjects, getExamTestTypes } from '../utils/examUtils';

const TestLayout = () => {
  const { activeExam, selectedExams } = useExam();

  if (!activeExam) {
    return (
      <div className="p-4 sm:p-8 text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
          No Exam Selected
        </h2>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Please select an exam from the dropdown above to see the layout test.
        </p>
      </div>
    );
  }

  const colors = getExamColors(activeExam.id);
  const subjects = getExamSubjects(activeExam.id);
  const testTypes = getExamTestTypes(activeExam.id);

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-7xl mx-auto">
      {/* Layout Test Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Layout Test - {activeExam.name}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Testing responsive layout and exam-specific content for {activeExam.fullName}
        </p>
      </div>

      {/* Responsive Grid Test */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {subjects.map((subject, index) => (
          <div 
            key={subject}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
            style={{ borderLeftColor: colors.primary, borderLeftWidth: '4px' }}
          >
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
              {subject}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Subject {index + 1} of {subjects.length}
            </p>
          </div>
        ))}
      </div>

      {/* Test Types */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Available Test Types
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testTypes.map((testType) => (
            <div 
              key={testType.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-primary-300 transition-colors"
            >
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                {testType.name}
              </h3>
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <p>Duration: {testType.duration} minutes</p>
                <p>Questions: {testType.questions}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Color Scheme Test */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Color Scheme Test
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div 
            className="p-4 rounded-lg text-white"
            style={{ backgroundColor: colors.primary }}
          >
            <h3 className="font-medium mb-2">Primary</h3>
            <p className="text-sm opacity-90">{colors.primary}</p>
          </div>
          <div 
            className="p-4 rounded-lg text-white"
            style={{ backgroundColor: colors.secondary }}
          >
            <h3 className="font-medium mb-2">Secondary</h3>
            <p className="text-sm opacity-90">{colors.secondary}</p>
          </div>
          <div 
            className="p-4 rounded-lg"
            style={{ backgroundColor: colors.accent, color: colors.text }}
          >
            <h3 className="font-medium mb-2">Accent</h3>
            <p className="text-sm opacity-90">{colors.accent}</p>
          </div>
          <div 
            className="p-4 rounded-lg text-white"
            style={{ backgroundColor: colors.text }}
          >
            <h3 className="font-medium mb-2">Text</h3>
            <p className="text-sm opacity-90">{colors.text}</p>
          </div>
        </div>
      </div>

      {/* Responsive Text Test */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Responsive Text Test
        </h2>
        <div className="space-y-4">
          <p className="text-sm sm:text-base lg:text-lg">
            This text should scale appropriately on different screen sizes.
          </p>
          <p className="text-xs sm:text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Smaller text that also scales responsively.
          </p>
          <div className="text-center">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
              Large Heading
            </h3>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              Centered text with responsive sizing
            </p>
          </div>
        </div>
      </div>

      {/* Selected Exams Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Selected Exams ({selectedExams.length})
        </h2>
        <div className="flex flex-wrap gap-2">
          {selectedExams.map((exam) => (
            <div
              key={exam.id}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                exam.id === activeExam.id
                  ? 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
              }`}
            >
              {exam.name}
            </div>
          ))}
        </div>
      </div>

      {/* Screen Size Indicator */}
      <div className="fixed bottom-4 right-4 bg-black text-white px-3 py-1 rounded-lg text-sm font-mono">
        <span className="sm:hidden">xs</span>
        <span className="hidden sm:inline md:hidden">sm</span>
        <span className="hidden md:inline lg:hidden">md</span>
        <span className="hidden lg:inline xl:hidden">lg</span>
        <span className="hidden xl:inline 2xl:hidden">xl</span>
        <span className="hidden 2xl:inline">2xl</span>
      </div>
    </div>
  );
};

export default TestLayout;
